import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { generateSessionHTML, attachSessionViewerInteractions, loadSessionBundle } from '../utils/sessionViewer';
import { Auth, cookieUtils } from '../utils';
import { getUserFromToken, setToken } from '../services/user';
import GoogleLogin from './GoogleLogin';

// Resolve API base robustly for prod/local
const SERVER_URL = (() => {
    const env = process.env.REACT_APP_SERVER_URL || (process.env as any).SERVER_URL || '';
    if (env) return env.replace(/\/$/, '');
    if (typeof window !== 'undefined') {
        const { hostname } = window.location;
        if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:8001';
        return '';
    }
    return '';
})();

const PLATFORM_BASE_URL = (process.env.REACT_APP_PLATFORM_URL || '').replace(/\/$/, '');
const STORAGE_BUCKET_NAME = 'diraq-contents';

const SessionDetailsPage: React.FC = () => {
    const { attemptId } = useParams<{ attemptId: string }>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [html, setHtml] = useState<string>('');
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [user, setUser] = useState<any | null>(null);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);

    // Read environment from sessionStorage (same as main app)
    const [environment] = useState<string>(() => {
        const stored = sessionStorage.getItem('fermi-environment');
        if (stored && ['local', 'staging', 'production'].includes(stored)) {
            return stored;
        }
        return 'production'; // Default
    });

    const googleLoginUrl = `${process.env.REACT_APP_PLATFORM_URL}/auth/google?redirect=${window.location.href}`;
    const token = cookieUtils.get(Auth.TOKEN);

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const urlToken = searchParams.get('token');
        if (urlToken) {
            setToken(urlToken);
        }
    }, []);

    useEffect(() => {
        getUserFromToken()
            .then((userData) => {
                if (userData && userData.id) {
                    setUser(userData);
                } else {
                    setUser(null);
                }
            })
            .catch((err) => {
                console.error('Error fetching user:', err);
                setUser(null);
            })
            .finally(() => {
                setIsCheckingAuth(false);
            });
    }, [token]);

    useEffect(() => {
        console.log('[UI] SessionDetailsPage: starting load', { attemptId, SERVER_URL, environment });
        if (!attemptId) {
            setError('No session ID provided');
            setLoading(false);
            return;
        }

        let isCancelled = false;
        const load = async () => {
            try {
                setLoading(true);
                setError(null);
                const { session, conversation, implicitAttempts } = await loadSessionBundle(
                    attemptId,
                    SERVER_URL || '',
                    environment
                );
                if (isCancelled) return;
                const htmlOut = generateSessionHTML(session, conversation, implicitAttempts);
                setHtml(htmlOut);
            } catch (err: any) {
                if (isCancelled) return;
                console.error('[UI] SessionDetailsPage: error loading session', err);
                setError(err?.message || 'Failed to load session');
            } finally {
                if (!isCancelled) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            isCancelled = true;
        };
    }, [attemptId, environment]);

    // Render HTML when container is present and html is ready
    useEffect(() => {
        if (loading || error) return;
        const container = containerRef.current || document.getElementById('session-container');
        if (!container) {
            console.warn('[UI] SessionDetailsPage: container not yet in DOM');
            return;
        }
        if (!html) {
            console.warn('[UI] SessionDetailsPage: html not ready to render');
            return;
        }

        let cleanup: (() => void) | undefined;
        try {
            // Clear any previous shadow root content
            // Create shadow root and inject
            const shadowRoot = (container as any).shadowRoot || container.attachShadow({ mode: 'open' });
            // Reset shadow content
            while (shadowRoot.firstChild) shadowRoot.removeChild(shadowRoot.firstChild);

            const wrapper = document.createElement('div');
            wrapper.innerHTML = html;
            const headElements = Array.from(wrapper.querySelectorAll('style, link[rel="stylesheet"]'));
            headElements.forEach((el) => shadowRoot.appendChild(el.cloneNode(true)));
            const bodyContent = wrapper.querySelector('body')?.innerHTML || wrapper.querySelector('.container')?.outerHTML;
            if (!bodyContent) throw new Error('No body content to render');
            const contentDiv = document.createElement('div');
            contentDiv.innerHTML = bodyContent;
            shadowRoot.appendChild(contentDiv);

            cleanup = attachSessionViewerInteractions(shadowRoot, {
                platformBaseUrl: PLATFORM_BASE_URL,
                authCookieName: Auth.TOKEN,
                bucketName: STORAGE_BUCKET_NAME,
            });
            console.log('[UI] SessionDetailsPage: render complete');
        } catch (renderErr: any) {
            console.error('[UI] SessionDetailsPage: render error', renderErr);
            setError(renderErr?.message || 'Failed to render session details');
        }

        return () => {
            if (cleanup) cleanup();
        };
    }, [loading, error, html]);

    // Show loading state while checking authentication
    if (isCheckingAuth) {
        return (
            <div style={{ padding: '50px', textAlign: 'center' }}>
                <h2>Checking authentication...</h2>
            </div>
        );
    }

    // Show login if not authenticated
    if (!user) {
        return (
            <div style={{ width: '100%', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <GoogleLogin
                    loginUrl={googleLoginUrl}
                    onLoginClick={() => console.log('Login initiated')}
                    isOpen={true}
                    onClose={() => {}}
                    disabled={false}
                    title="Sign in to view session details"
                    description="Please sign in with your Google account to access this session details viewer."
                />
            </div>
        );
    }

    if (loading) {
        return (
            <div style={{ padding: '50px', textAlign: 'center' }}>
                <h2>Loading session...</h2>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '50px', textAlign: 'center', color: 'red' }}>
                <h2>Error</h2>
                <p>{error}</p>
            </div>
        );
    }

    return <div id="session-container" ref={containerRef} style={{ width: '100%', height: '100vh' }}></div>;
};

export default SessionDetailsPage;
